import type { BatchActionForm, TableRow } from '@/components/table/types'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useToast } from '@/composables/useToast'
import { API_BASE } from '@/constants'
import { ref } from 'vue'

const batchActionLoading = ref(false)
const batchActionSuccess = ref(false)
const batchActionError = ref(false)
const batchActionErrorMessage = ref('')
const skippedAccounts = ref([])

/**
 * Shared batch action execution logic for accounts.
 * Used across legacy (useAccounts), mapbox (useMapboxAccountsApi), and redis (useRedisAccountsApi) implementations.
 */
export function useAccountsBatchActions() {
	const { bugsnagReport } = useErrorReporter()

	const performBatchAction = async (formData: BatchActionForm) => {
		batchActionLoading.value = true
		const { addToast } = useToast()

		const actionEndpoint = formData.actionOption?.endpoint
		const { url, params } = actionEndpoint || {}
		const itemIds = formData.selectedItems?.map((item: TableRow) => item.id) || formData.selectedIds || []

		const requestBody = {
			accountIds: itemIds,
			scheduleType: params?.scheduleType || '',
			sendEmail: params?.sendEmail || false,
			sendSms: params?.sendSms || false
		}

		await $fetch(`${API_BASE}${url}`, {
			method: 'POST',
			credentials: 'include',
			body: requestBody,
			onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.errors?.[0]?.title || `Error performing ${formData.actionOption?.label}.`)
				}

				if (response._data.skippedAccounts.length) {
					skippedAccounts.value = response._data.skippedAccounts
				} else {
					skippedAccounts.value = []
				}

				if (!response._data?.success) {
					throw new Error('Email/SMS was already sent recently.')
				}

				batchActionSuccess.value = true
				if (formData.showSuccessToast) {
                    if(response._data?.landLineFailure) {
                        addToast({
                            notificationType: 'error',
                            title: 'Warning',
                            message: 'Phone Number(s) are not mobile.'
                        })
                    } else {
                        addToast({
                            notificationType: 'success',
                            title: 'Success!',
                            message: `${formData.actionOption?.label} completed successfully.`
                        })
                    }
				}
			}
		}).catch(e => {
			batchActionError.value = true
			batchActionErrorMessage.value = e.message || 'Error performing batch action'
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error performing batch action',
					message: e.message
				}
			})
		}).finally(() => {
			batchActionLoading.value = false
		})
	}

	const clearBatchActionState = () => {
		batchActionLoading.value = false
		batchActionSuccess.value = false
		batchActionError.value = false
		batchActionErrorMessage.value = ''
		skippedAccounts.value = []
	}

	return {
		performBatchAction,
		clearBatchActionState,
		batchActionLoading,
		batchActionSuccess,
		batchActionError,
		batchActionErrorMessage,
		skippedAccounts
	}
}
